import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  ChartNoAxesCombined,
  Flame,
  BookOpen,
  Briefcase,
  ChevronDown,
  FileText,
  FolderKanban,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  LogOut,
  Search,
  Settings2,
  Sparkles,
  Target,
  Menu,
  ChevronRight,
  Trophy,
  UserRound,
  Users,
  X,
} from "lucide-react";

import {
  endDayAttendance,
  getAttendanceMe,
  getGamificationDashboard,
  getGlobalSearch,
  getKpiAssignmentStatus,
  getLearningAssignments,
  getLeaveRequests,
  getNotifications,
  getProjects,
  markNotificationsRead,
} from "@/lib/api";
import { isNotificationCountEnabled, matchesNotificationTab, withNotificationUiFields } from "@/lib/notifications";
import { canSeeOrgEditor, canSeeScorecard, resolveOrgLevel } from "@/lib/org";
import { useShellCache } from "@/context/ShellCacheContext";
import { AddTaskModal } from "@/components/AddTaskModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROUTE_TITLE = {
  "/dashboard": "Dashboard",
  "/tasks": "Tasks",
  "/projects": "Projects",
  "/learning": "Courses",
  "/hr": "Leaves and HR",
  "/clients": "Clients",
  "/brief": "Brand Builder",
  "/profile": "Profile",
  "/profile/leaves": "My Leaves",
  "/profile/stats": "My Work",
  "/profile/notifications": "Notifications",
  "/notifications": "Notifications",
  "/scorecard": "Manager Dashboard",
  "/manager-dashboard": "Manager Dashboard",
  "/finance": "Receivables",
  "/contracts-renewals": "Contracts",
  "/manager-goals": "Goals and Sales",
  "/kpi-tracker": "Retainer KPI Tracker",
  "/my-kpi-targets": "Retainer KPI Tracker",
  "/leaderboard": "Attendance Leaderboard",
  "/workshops": "Conduct Workshops",
  "/org-editor": "Org Structure Editor",
  "/audit-logs": "Audit Logs",
};

const SEARCH_GROUP_ORDER = ["people", "clients", "projects", "files", "tasks"];

const SEARCH_GROUP_META = {
  people: { label: "People" },
  clients: { label: "Clients" },
  projects: { label: "Projects" },
  files: { label: "Files & links" },
  tasks: { label: "Tasks" },
};

const STATUS_LABEL_MAP = {
  to_do: "To do",
  in_progress: "In progress",
  completed: "Completed",
};
const CLIENTS_SECTION_COLLAPSE_STORAGE_KEY = "dimerse-sidebar-clients-collapsed";
const SIDEBAR_COLLAPSE_STORAGE_KEY = "dimerse-sidebar-collapsed";
const PROJECT_VIEW_COUNTS_STORAGE_KEY = "dimerse-sidebar-project-view-counts";

const iconClass = "h-4 w-4";

const emptySearchResults = {
  people: [],
  clients: [],
  projects: [],
  files: [],
  tasks: [],
};

const initials = (value = "") => {
  const clean = value.trim();
  if (!clean) return "NA";
  const chunks = clean.split(" ");
  if (chunks.length === 1) return chunks[0].slice(0, 2).toUpperCase();
  return `${chunks[0][0]}${chunks[chunks.length - 1][0]}`.toUpperCase();
};

const profileAvatar = (profile = {}) => {
  const mode = String(profile.avatar_mode || "").trim().toLowerCase();
  if (mode === "picture" && String(profile.picture || "").trim()) return "";
  const emoji = String(profile.avatar_emoji || "").trim();
  if (emoji) return emoji;
  const customInitials = String(profile.initials || "").trim();
  if (customInitials) return customInitials.toUpperCase();
  return initials(profile.name || profile.email || "");
};

const profileAvatarPicture = (profile = {}) => {
  const mode = String(profile.avatar_mode || "").trim().toLowerCase();
  const picture = String(profile.picture || "").trim();
  if (!picture) return "";
  if (mode === "picture") return picture;
  if (!mode && !String(profile.avatar_emoji || "").trim()) return picture;
  return "";
};

const profileAvatarStyle = (profile = {}) => {
  if (profileAvatarPicture(profile)) return undefined;
  if (String(profile.avatar_emoji || "").trim()) return undefined;
  const bg = String(profile.initials_bg || "").trim();
  const text = String(profile.initials_text || "").trim();
  if (bg && text) return { background: bg, color: text };
  return undefined;
};

const healthDotClass = (score) => {
  if (score >= 80) return "dot dot-ok";
  if (score >= 65) return "dot dot-warn";
  if (score >= 50) return "dot dot-risk";
  return "dot dot-crit";
};

const healthBandLabel = (band) => {
  if (band === "healthy") return "Healthy";
  if (band === "watch") return "Watch";
  if (band === "at_risk") return "At risk";
  return "Critical";
};

const readProjectViewCounts = () => {
  try {
    const raw = localStorage.getItem(PROJECT_VIEW_COUNTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const writeProjectViewCount = (projectId) => {
  if (!projectId) return;
  try {
    const counts = readProjectViewCounts();
    counts[projectId] = Number(counts[projectId] || 0) + 1;
    localStorage.setItem(PROJECT_VIEW_COUNTS_STORAGE_KEY, JSON.stringify(counts));
  } catch (_error) {
    // no-op for private browsing/local storage restrictions
  }
};

const buildMostViewedProjectLinks = (projects = []) => {
  const counts = readProjectViewCounts();
  const rows = [...projects]
    .filter((project) => String(project?.id || "").trim().length > 0)
    .sort((a, b) => {
      const aViews = Number(counts[a.id] || 0);
      const bViews = Number(counts[b.id] || 0);
      if (bViews !== aViews) return bViews - aViews;
      const aActive = String(a.status || "active").toLowerCase() === "active" ? 1 : 0;
      const bActive = String(b.status || "active").toLowerCase() === "active" ? 1 : 0;
      if (bActive !== aActive) return bActive - aActive;
      return String(a.name || "").localeCompare(String(b.name || ""), "en", { sensitivity: "base" });
    })
    .slice(0, 3);

  return rows.map((project) => ({
    id: project.id,
    name: project.name,
    client_name: project.client_name,
    health_score: Number(project.health_score || 0),
    view_count: Number(counts[project.id] || 0),
  }));
};

const fileTypeStyle = (fileType = "") => {
  const normalized = String(fileType).toUpperCase();
  if (normalized === "PDF") return { bg: "#FEE2E2", text: "#B91C1C" };
  if (normalized === "PPTX") return { bg: "#FFEDD5", text: "#C2410C" };
  if (normalized === "DOC") return { bg: "#DBEAFE", text: "#1D4ED8" };
  if (normalized === "SHEET") return { bg: "#DCFCE7", text: "#166534" };
  return { bg: "#E2E8F0", text: "#334155" };
};

const projectIconColor = (band) => {
  if (band === "healthy") return "#10B981";
  if (band === "watch") return "#F59E0B";
  if (band === "at_risk") return "#F97316";
  return "#EF4444";
};

const renderHighlightedTitle = (text, query) => {
  const source = String(text || "");
  const needle = String(query || "").trim();
  if (needle.length < 1) return source;
  const lowerSource = source.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const index = lowerSource.indexOf(lowerNeedle);
  if (index < 0) return source;

  const before = source.slice(0, index);
  const match = source.slice(index, index + needle.length);
  const after = source.slice(index + needle.length);
  return (
    <>
      {before}
      <mark style={{ background: "#FEF08A", color: "#1F2937", borderRadius: 3, paddingInline: 1 }}>{match}</mark>
      {after}
    </>
  );
};

const formatSearchDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

export default function AppShell({ user, onLogout, children, topbarExtra = null, hideGlobalNewTask = false, topbarSearchPlaceholder = "Search tasks, projects..." }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [navBadges, setNavBadges] = useState({ tasks: 0, projects: 0, learning: 0, hr: 0 });
  const [projectQuickLinks, setProjectQuickLinks] = useState([]);
  const [accessibleProjects, setAccessibleProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(emptySearchResults);
  const [isSearchTrayOpen, setIsSearchTrayOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [isNotificationsTrayOpen, setIsNotificationsTrayOpen] = useState(false);
  const [activeNotificationTab, setActiveNotificationTab] = useState("all");
  const [isClientsSectionCollapsed, setIsClientsSectionCollapsed] = useState(() => {
    try {
      return localStorage.getItem(CLIENTS_SECTION_COLLAPSE_STORAGE_KEY) === "1";
    } catch (_error) {
      return false;
    }
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === "1";
    } catch (_error) {
      return false;
    }
  });
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [attendanceRowToday, setAttendanceRowToday] = useState(null);
  const [isEndingDay, setIsEndingDay] = useState(false);
  const [isEndDayConfirmOpen, setIsEndDayConfirmOpen] = useState(false);
  const [gamificationHeader, setGamificationHeader] = useState(null);
  const [isStreakPopoverOpen, setIsStreakPopoverOpen] = useState(false);
  const [isBadgePopoverOpen, setIsBadgePopoverOpen] = useState(false);
  const [showMyKpiTargetsNav, setShowMyKpiTargetsNav] = useState(false);

  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const notificationWrapRef = useRef(null);
  const streakWrapRef = useRef(null);
  const badgeWrapRef = useRef(null);

  const canAccessClients = true;
  const canAccessWorkshops = resolveOrgLevel(user) <= 2;
  const canAccessTools = true;
  const canAccessAuditLogs = user.role === "super_admin";
  const financeSidebarOnly = false;
  const canCreateTask = ["super_admin", "project_admin", "account_manager"].includes(user.role);
  const showScorecardNav = canSeeScorecard(user);
  const showOrgEditorNav = canSeeOrgEditor(user);
  const showManagerGoalsNav = resolveOrgLevel(user) <= 2;
  const showKpiTrackerNav = resolveOrgLevel(user) <= 2;
  const showContractsRenewalsNav = resolveOrgLevel(user) <= 2;
  const showRetainerKpiTrackerNav = user.role === "super_admin" || showKpiTrackerNav || showMyKpiTargetsNav;
  const retainerKpiRoute = resolveOrgLevel(user) <= 2 || user.role === "super_admin" ? "/kpi-tracker" : "/my-kpi-targets";
  const {
    getSidebarMeta,
    setSidebarMeta,
    getNotifications: getCachedNotifications,
    setNotifications: setCachedNotifications,
  } = useShellCache();

  const activeProjectId = useMemo(() => {
    const match = location.pathname.match(/^\/project\/([^/?]+)/);
    return match?.[1] || "";
  }, [location.pathname]);

  const pageTitle = useMemo(() => {
    if (location.pathname.startsWith("/project/")) return "Project Detail";
    if (location.pathname.startsWith("/pod-detail/")) return "POD Detail";
    return ROUTE_TITLE[location.pathname] || "Dashboard";
  }, [location.pathname]);

  const groupedSearchResults = useMemo(
    () => SEARCH_GROUP_ORDER.map((type) => ({ type, label: SEARCH_GROUP_META[type].label, items: searchResults[type] || [] })),
    [searchResults],
  );

  const flattenedSearchResults = useMemo(
    () =>
      groupedSearchResults.flatMap((group) =>
        group.items.map((item) => ({ type: group.type, item, key: `${group.type}-${item.id || item.task_id || item.name}` })),
      ),
    [groupedSearchResults],
  );

  const totalSearchResults = flattenedSearchResults.length;

  const notificationPrefs = user?.notification_prefs || {};

  const unreadNotificationCount = useMemo(
    () =>
      notifications.filter(
        (item) => item.status !== "read" && isNotificationCountEnabled(item, notificationPrefs),
      ).length,
    [notificationPrefs, notifications],
  );

  const actionableUnreadNotifications = useMemo(
    () =>
      notifications.filter(
        (item) => item.status !== "read" && isNotificationCountEnabled(item, notificationPrefs),
      ),
    [notificationPrefs, notifications],
  );

  const trayNotifications = useMemo(
    () => notifications.filter((item) => matchesNotificationTab(item, activeNotificationTab)).slice(0, 8),
    [activeNotificationTab, notifications],
  );

  const attendanceSessionMeta = useMemo(() => {
    const first = attendanceRowToday?.session_started_at
      ? new Date(attendanceRowToday.session_started_at)
      : attendanceRowToday?.first_activity_at
        ? new Date(attendanceRowToday.first_activity_at)
        : null;
    const ended = attendanceRowToday?.end_day_at ? new Date(attendanceRowToday.end_day_at) : null;
    const hasStarted = first && !Number.isNaN(first.getTime());
    const hasEnded = ended && !Number.isNaN(ended.getTime());
    const isAutoClosed = attendanceRowToday?.end_day_mode === "auto";

    if (!hasStarted) {
      return {
        actionLabel: hasEnded ? "End day again" : "End day",
        rangeLabel: hasEnded ? `Ended at ${ended.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }).toLowerCase()}` : "Not saved yet",
        isEnded: Boolean(hasEnded),
        summary: null,
      };
    }

    const endPoint = hasEnded ? ended : first;
    const elapsedMinutes = Math.max(0, Math.floor((endPoint.getTime() - first.getTime()) / 60000));
    const hours = Math.floor(elapsedMinutes / 60);
    const minutes = elapsedMinutes % 60;
    const runtime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    const startedLabel = first.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }).toLowerCase();
    const endLabel = hasEnded
      ? `Started at ${startedLabel} · ended ${ended.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }).toLowerCase()}`
      : `Started at ${startedLabel} · Not saved yet`;

    return {
      actionLabel: hasEnded ? "End day again" : "End day",
      rangeLabel: endLabel,
      isEnded: hasEnded,
      summary: hasEnded
        ? `${attendanceRowToday?.activity_count || 0} actions · ${attendanceRowToday?.projects_touched || 0} projects · ${runtime}${isAutoClosed ? " · auto-closed 9:00pm IST" : ""}`
        : null,
    };
  }, [attendanceRowToday]);

  useEffect(() => {
    let alive = true;
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const weekParam = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}-${String(weekStart.getDate()).padStart(2, "0")}`;
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const loadAttendance = async () => {
      try {
        const response = await getAttendanceMe({ week: weekParam });
        if (!alive) return;
        const todayRow = (response.records || []).find((row) => row.date === todayKey) || null;
        setAttendanceRowToday(todayRow);
      } catch (_error) {
        if (!alive) return;
        setAttendanceRowToday(null);
      }
    };

    loadAttendance();
    return () => {
      alive = false;
    };
  }, [user.email]);

  useEffect(() => {
    let alive = true;
    const loadKpiAssignmentStatus = async () => {
      if (resolveOrgLevel(user) <= 2) {
        if (alive) setShowMyKpiTargetsNav(false);
        return;
      }
      try {
        const response = await getKpiAssignmentStatus();
        if (!alive) return;
        setShowMyKpiTargetsNav(Boolean(response?.has_assignments));
      } catch (_error) {
        if (!alive) return;
        setShowMyKpiTargetsNav(false);
      }
    };
    loadKpiAssignmentStatus();
    return () => {
      alive = false;
    };
  }, [user.email, user.org_level]);

  const executeEndDay = async () => {
    setIsEndingDay(true);
    try {
      const response = await endDayAttendance();
      setAttendanceRowToday((prev) => ({
        ...(prev || {}),
        date: response.date,
        status: response.status,
        end_day_at: response.end_day_at,
        end_day_mode: response.end_day_mode,
        activity_count: response.activity_count,
        projects_touched: response.projects_touched,
      }));
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("attendance-session-updated", {
            detail: {
              date: response.date,
              status: response.status,
              end_day_at: response.end_day_at,
              end_day_mode: response.end_day_mode,
              activity_count: response.activity_count,
              projects_touched: response.projects_touched,
            },
          }),
        );
      }
    } catch (_error) {
      // Intentionally silent in shell
    } finally {
      setIsEndingDay(false);
    }
  };

  const handleEndDay = async () => {
    if (isEndingDay) return;
    if (attendanceSessionMeta.isEnded) {
      setIsEndDayConfirmOpen(true);
      return;
    }
    await executeEndDay();
  };

  useEffect(() => {
    let alive = true;
    const cacheKey = user.email || user.workspace_domain || "anonymous";

    const cachedMeta = getSidebarMeta(cacheKey);
    if (cachedMeta) {
      setNavBadges(cachedMeta.navBadges || { tasks: 0, projects: 0, learning: 0, hr: 0 });
      setProjectQuickLinks(cachedMeta.projectQuickLinks || cachedMeta.clientQuickLinks || []);
      setAccessibleProjects(cachedMeta.accessibleProjects || []);
    }

    const loadSidebarMeta = async () => {
      try {
        const [projectRows, learningRows, leaveRows, unreadNotificationRows] = await Promise.all([
          getProjects(),
          getLearningAssignments(),
          getLeaveRequests({ status: "pending" }),
          getNotifications({ status: "unread", limit: 200 }),
        ]);

        if (!alive) return;

        const actionableUnread = (unreadNotificationRows || []).filter(
          (item) => item.status !== "read" && isNotificationCountEnabled(item, notificationPrefs),
        );
        const unreadTasks = actionableUnread.filter((item) => item.kind === "tasks").length;
        const unreadProjects = actionableUnread.filter((item) => item.kind === "projects").length;
        const unreadLearning = actionableUnread.filter((item) => item.kind === "learning").length;
        const unreadHr = actionableUnread.filter((item) => ["leaves", "members", "roles"].includes(String(item.kind || ""))).length;
        const learningAwaitingApproval = learningRows.filter((item) => String(item.status || "").toLowerCase() === "submitted").length;

        setNavBadges({
          tasks: unreadTasks,
          projects: unreadProjects,
          learning: learningAwaitingApproval + unreadLearning,
          hr: leaveRows.length + unreadHr,
        });

        setAccessibleProjects(projectRows || []);
        const nextProjectQuickLinks = buildMostViewedProjectLinks(projectRows || []);
        setProjectQuickLinks(nextProjectQuickLinks);
        setSidebarMeta(cacheKey, {
          navBadges: {
            tasks: unreadTasks,
            projects: unreadProjects,
            learning: learningAwaitingApproval + unreadLearning,
            hr: leaveRows.length + unreadHr,
          },
          projectQuickLinks: nextProjectQuickLinks,
          accessibleProjects: projectRows || [],
        });
      } catch (_error) {
        if (!alive) return;
      }
    };

    if (!cachedMeta) {
      loadSidebarMeta();
    }
    return () => {
      alive = false;
    };
  }, [getSidebarMeta, notificationPrefs, setSidebarMeta, user.email, user.workspace_domain]);

  useEffect(() => {
    const match = location.pathname.match(/^\/project\/([^/]+)$/);
    if (!match) return;
    const projectId = decodeURIComponent(match[1] || "");
    if (!projectId) return;

    writeProjectViewCount(projectId);
    const nextProjectQuickLinks = buildMostViewedProjectLinks(accessibleProjects || []);
    setProjectQuickLinks(nextProjectQuickLinks);

    const cacheKey = user.email || user.workspace_domain || "anonymous";
    setSidebarMeta(cacheKey, {
      navBadges,
      projectQuickLinks: nextProjectQuickLinks,
      accessibleProjects: accessibleProjects || [],
    });
  }, [accessibleProjects, location.pathname, navBadges, setSidebarMeta, user.email, user.workspace_domain]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults(emptySearchResults);
      setIsSearchLoading(false);
      setActiveSearchIndex(-1);
      setIsSearchTrayOpen(false);
      return undefined;
    }

    let alive = true;
    const timeoutId = window.setTimeout(async () => {
      setIsSearchLoading(true);
      try {
        const response = await getGlobalSearch({ q: trimmed, workspace: user.workspace_domain });
        const people = (response.people || []).map((person) => {
          const isCurrentUser = (person.id && user.id && person.id === user.id) || person.name === user.name;
          if (!isCurrentUser) return person;
          return {
            ...person,
            avatar_emoji: user.avatar_emoji || person.avatar_emoji,
            picture: user.picture || person.picture,
            avatar_mode: user.avatar_mode || person.avatar_mode,
            initials: user.initials || person.initials,
            palette: user.initials_bg && user.initials_text
              ? { bg: user.initials_bg, text: user.initials_text }
              : person.palette,
          };
        });
        const activeProjects = (accessibleProjects || []).filter((project) => !["archived", "completed"].includes(String(project.status || "").toLowerCase()));
        const activeProjectIds = new Set(activeProjects.map((project) => String(project.id || "")).filter(Boolean));
        const activeClientIds = new Set(activeProjects.map((project) => String(project.client_id || "")).filter(Boolean));
        const filteredProjects = (response.projects || []).filter((project) => {
          const projectId = String(project.id || "");
          if (!projectId || !activeProjectIds.size) return true;
          return activeProjectIds.has(projectId);
        });
        const filteredClients = (response.clients || []).filter((client) => {
          const clientId = String(client.id || "");
          if (!clientId || !activeClientIds.size) return true;
          return activeClientIds.has(clientId);
        });
        if (!alive) return;
        setSearchResults({
          people,
          clients: filteredClients,
          projects: filteredProjects,
          files: response.files || [],
          tasks: response.tasks || [],
        });
        setIsSearchTrayOpen(true);
        setActiveSearchIndex(0);
      } catch (_error) {
        if (!alive) return;
        setSearchResults(emptySearchResults);
        setActiveSearchIndex(-1);
      } finally {
        if (alive) setIsSearchLoading(false);
      }
    }, 200);

    return () => {
      alive = false;
      clearTimeout(timeoutId);
    };
  }, [searchQuery, user.workspace_domain, accessibleProjects]);

  useEffect(() => {
    const keyHandler = (event) => {
      const targetTag = String(event.target?.tagName || "").toLowerCase();
      const isTypingContext = targetTag === "input" || targetTag === "textarea" || event.target?.isContentEditable;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
        setIsSearchTrayOpen(searchQuery.trim().length >= 2);
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "n") {
        if (!hideGlobalNewTask && canCreateTask) {
          event.preventDefault();
          setIsAddTaskModalOpen(true);
        }
        return;
      }

      if (!isTypingContext && event.key === "/") {
        event.preventDefault();
        searchInputRef.current?.focus();
        setIsSearchTrayOpen(searchQuery.trim().length >= 2);
      }
    };
    window.addEventListener("keydown", keyHandler);
    return () => window.removeEventListener("keydown", keyHandler);
  }, [canCreateTask, hideGlobalNewTask, searchQuery]);

  useEffect(() => {
    const clickOutside = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setIsSearchTrayOpen(false);
      }
      if (notificationWrapRef.current && !notificationWrapRef.current.contains(event.target)) {
        setIsNotificationsTrayOpen(false);
      }
      if (streakWrapRef.current && !streakWrapRef.current.contains(event.target)) {
        setIsStreakPopoverOpen(false);
      }
      if (badgeWrapRef.current && !badgeWrapRef.current.contains(event.target)) {
        setIsBadgePopoverOpen(false);
      }
    };
    window.addEventListener("mousedown", clickOutside);
    return () => window.removeEventListener("mousedown", clickOutside);
  }, []);

  useEffect(() => {
    let alive = true;
    const loadGamificationHeader = async () => {
      try {
        const response = await getGamificationDashboard();
        if (!alive) return;
        setGamificationHeader(response);
      } catch (_error) {
        if (!alive) return;
        setGamificationHeader(null);
      }
    };
    loadGamificationHeader();

    const refreshOnAttendance = () => loadGamificationHeader();
    window.addEventListener("attendance-session-updated", refreshOnAttendance);
    return () => {
      alive = false;
      window.removeEventListener("attendance-session-updated", refreshOnAttendance);
    };
  }, [user.email]);

  useEffect(() => {
    if (!totalSearchResults) {
      setActiveSearchIndex(-1);
      return;
    }
    setActiveSearchIndex((prev) => {
      if (prev < 0) return 0;
      if (prev >= totalSearchResults) return totalSearchResults - 1;
      return prev;
    });
  }, [totalSearchResults]);

  const handleSearchSelect = (type, item) => {
    if (type === "people") {
      setIsSearchTrayOpen(false);
      return;
    }

    if (type === "clients") {
      navigate(`/project/${encodeURIComponent(item.id)}/update-client-view`);
    } else if (type === "projects") {
      navigate(`/project/${item.id}`);
    } else if (type === "files") {
      navigate(`/project/${item.project_id}?focus=links&file=${encodeURIComponent(item.id)}`);
    } else if (type === "tasks") {
      navigate(`/tasks?task_id=${encodeURIComponent(item.id)}`);
    }

    setSearchQuery("");
    setSearchResults(emptySearchResults);
    setIsSearchTrayOpen(false);
    setActiveSearchIndex(-1);
  };

  const onSearchKeyDown = (event) => {
    if (!isSearchTrayOpen || totalSearchResults === 0) {
      if (event.key === "Escape") {
        setIsSearchTrayOpen(false);
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSearchIndex((prev) => (prev + 1) % totalSearchResults);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSearchIndex((prev) => (prev - 1 + totalSearchResults) % totalSearchResults);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const target = flattenedSearchResults[Math.max(0, activeSearchIndex)] || flattenedSearchResults[0];
      if (target) handleSearchSelect(target.type, target.item);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setIsSearchTrayOpen(false);
    }
  };

  const loadNotifications = useCallback(async (options = { forceRefresh: false }) => {
    const cacheKey = user.email || user.workspace_domain || "anonymous";
    const cachedNotifications = options.forceRefresh ? null : getCachedNotifications(cacheKey);
    if (cachedNotifications) {
      setNotifications(cachedNotifications);
      return cachedNotifications;
    }

    setIsNotificationsLoading(true);
    try {
      const rows = await getNotifications({ limit: 80 });
      const formattedRows = withNotificationUiFields(rows || []);
      setNotifications(formattedRows);
      setCachedNotifications(cacheKey, formattedRows);
      return formattedRows;
    } catch (_error) {
      setNotifications([]);
      return [];
    } finally {
      setIsNotificationsLoading(false);
    }
  }, [user.email, user.workspace_domain]);

  const markTrayNotificationsRead = useCallback(async (sourceRows = notifications) => {
    const unreadIds = sourceRows.filter((item) => item.status !== "read").map((item) => item.id);
    if (!unreadIds.length) return;
    try {
      const actionableUnread = sourceRows.filter(
        (item) => item.status !== "read" && isNotificationCountEnabled(item, notificationPrefs),
      );
      const delta = {
        tasks: actionableUnread.filter((item) => item.kind === "tasks").length,
        projects: actionableUnread.filter((item) => item.kind === "projects").length,
        learning: actionableUnread.filter((item) => item.kind === "learning").length,
        hr: actionableUnread.filter((item) => ["leaves", "members", "roles"].includes(String(item.kind || ""))).length,
      };
      await markNotificationsRead({ ids: unreadIds });
      setNotifications((prev) => prev.map((item) => ({ ...item, status: "read" })));
      setNavBadges((prev) => ({
        ...prev,
        tasks: Math.max(0, Number(prev.tasks || 0) - delta.tasks),
        projects: Math.max(0, Number(prev.projects || 0) - delta.projects),
        learning: Math.max(0, Number(prev.learning || 0) - delta.learning),
        hr: Math.max(0, Number(prev.hr || 0) - delta.hr),
      }));
    } catch (_error) {
      // no-op to avoid blocking tray usage
    }
  }, [notificationPrefs, notifications]);

  const toggleNotificationsTray = useCallback(async () => {
    if (isNotificationsTrayOpen) {
      setIsNotificationsTrayOpen(false);
      return;
    }
    const rows = await loadNotifications({ forceRefresh: true });
    setIsNotificationsTrayOpen(true);
    await markTrayNotificationsRead(rows);
  }, [isNotificationsTrayOpen, loadNotifications, markTrayNotificationsRead]);

  const openNotificationLink = (item) => {
    setIsNotificationsTrayOpen(false);
    navigate(item?.link || "/notifications");
  };

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    setIsNotificationsTrayOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(CLIENTS_SECTION_COLLAPSE_STORAGE_KEY, isClientsSectionCollapsed ? "1" : "0");
    } catch (_error) {
      // no-op for private browsing/local storage restrictions
    }
  }, [isClientsSectionCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, isSidebarCollapsed ? "1" : "0");
    } catch (_error) {
      // no-op for private browsing/local storage restrictions
    }
  }, [isSidebarCollapsed]);

  const trayTabCount = (tab) => actionableUnreadNotifications.filter((item) => matchesNotificationTab(item, tab)).length;

  const streakSummary = gamificationHeader?.streak || {};
  const rolePanels = gamificationHeader?.role_panels || [];
  const preferredRole = gamificationHeader?.role_default || "account_manager";
  const activeRolePanel = rolePanels.find((panel) => panel.role === preferredRole) || rolePanels[0] || { badges: [], earned_count: 0, role_label: "Account Manager" };
  const earnedBadges = Number(activeRolePanel.earned_count || 0);
  const earnedBadgeRows = (activeRolePanel.badges || []).filter((badge) => badge.earned);

  const navClass = ({ isActive }) => `nav-item nav-link ${isActive ? "active" : ""}`;

  return (
    <div className="app-shell" data-testid="app-shell-root">
      <aside className={`sidebar ${isSidebarCollapsed ? "is-collapsed" : ""}`} data-testid="app-shell-sidebar">
        <div className="sidebar-header" data-testid="sidebar-header">
          <div className="logo-mark" data-testid="sidebar-logo-mark">DJ</div>
          <div className="logo-text" data-testid="sidebar-logo-text-wrap">
            <div className="name" data-testid="sidebar-logo-name">Dimerse Digital</div>
            <div className="sub" data-testid="sidebar-logo-sub">Connect Ahead</div>
          </div>
          <button
            type="button"
            className="sidebar-collapse"
            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
            data-testid="sidebar-collapse-toggle-button"
            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>

        <nav className="sidebar-nav" data-testid="sidebar-navigation">
          {financeSidebarOnly ? (
            <>
              <NavLink to="/finance" className={navClass} data-testid="nav-finance-link">
                <LayoutDashboard className={iconClass} />
                <span className="nav-item-label">Receivables</span>
              </NavLink>
              <NavLink to="/leaderboard" className={navClass} data-testid="nav-leaderboard-link">
                <Trophy className={iconClass} />
                <span className="nav-item-label">Attendance Leaderboard</span>
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to="/dashboard" className={navClass} data-testid="nav-dashboard-link">
                <LayoutDashboard className={iconClass} />
                <span className="nav-item-label">Dashboard</span>
              </NavLink>

              <NavLink to="/tasks" className={navClass} data-testid="nav-tasks-link">
                <ListChecks className={iconClass} />
                <span className="nav-item-label">Tasks</span>
                <span className="nav-badge" data-testid="nav-tasks-link-badge">{navBadges.tasks}</span>
              </NavLink>

              <div className="nav-section-label" data-testid="nav-section-project-management">Project Management</div>
              <NavLink to="/projects" className={navClass} data-testid="nav-projects-link">
                <FolderKanban className={iconClass} />
                <span className="nav-item-label">Projects</span>
                <span className="nav-badge" data-testid="nav-projects-link-badge">{navBadges.projects}</span>
              </NavLink>

              {canAccessClients ? (
                <NavLink to="/clients" className={navClass} data-testid="nav-clients-link">
                  <Briefcase className={iconClass} />
                  <span className="nav-item-label">Clients</span>
                </NavLink>
              ) : null}

              <div className="nav-section-label" data-testid="nav-section-learning">Learning</div>
              {canAccessWorkshops ? (
                <NavLink to="/workshops" className={navClass} data-testid="nav-workshops-link">
                  <BookOpen className={iconClass} />
                  <span className="nav-item-label">Conduct Workshops</span>
                </NavLink>
              ) : null}

              <NavLink to="/learning" className={navClass} data-testid="nav-learning-link">
                <BookOpen className={iconClass} />
                <span className="nav-item-label">Courses</span>
                <span className="nav-badge indigo" data-testid="nav-learning-link-badge">{navBadges.learning}</span>
              </NavLink>

              <div className="nav-section-label" data-testid="nav-section-hr-and-people">HR and People</div>

              <NavLink to="/hr" className={navClass} data-testid="nav-hr-link">
                <Users className={iconClass} />
                <span className="nav-item-label">Leaves and HR</span>
                <span className="nav-badge warn" data-testid="nav-hr-link-badge">{navBadges.hr}</span>
              </NavLink>

              <NavLink to="/leaderboard" className={navClass} data-testid="nav-leaderboard-link">
                <Trophy className={iconClass} />
                <span className="nav-item-label">Attendance Leaderboard</span>
              </NavLink>

              {showOrgEditorNav ? (
                <NavLink to="/org-editor" className={navClass} data-testid="nav-org-editor-link">
                  <Users className={iconClass} />
                  <span className="nav-item-label">Org Editor</span>
                </NavLink>
              ) : null}

              {showScorecardNav || showManagerGoalsNav || showRetainerKpiTrackerNav ? <div className="nav-section-label" data-testid="nav-section-manage">Manage</div> : null}
              {showScorecardNav ? (
                <NavLink to="/manager-dashboard" className={navClass} data-testid="nav-scorecard-link">
                  <ChartNoAxesCombined className={iconClass} />
                  <span className="nav-item-label">Manager Dashboard</span>
                </NavLink>
              ) : null}
              {showManagerGoalsNav ? (
                <NavLink to="/manager-goals" className={navClass} data-testid="nav-manager-goals-link">
                  <Target className={iconClass} />
                  <span className="nav-item-label">Goals and Sales</span>
                </NavLink>
              ) : null}
              {showRetainerKpiTrackerNav ? (
                <NavLink to={retainerKpiRoute} className={navClass} data-testid="nav-retainer-kpi-tracker-link">
                  <Settings2 className={iconClass} />
                  <span className="nav-item-label">Retainer KPI Tracker</span>
                </NavLink>
              ) : null}

              {user.role === "super_admin" || showContractsRenewalsNav ? <div className="nav-section-label" data-testid="nav-section-finance">Finance</div> : null}
              {user.role === "super_admin" ? (
                <NavLink to="/finance" className={navClass} data-testid="nav-finance-link">
                  <LayoutDashboard className={iconClass} />
                  <span className="nav-item-label">Receivables</span>
                </NavLink>
              ) : null}
              {showContractsRenewalsNav ? (
                <NavLink to="/contracts-renewals" className={navClass} data-testid="nav-contracts-renewals-link">
                  <FileText className={iconClass} />
                  <span className="nav-item-label">Contracts</span>
                </NavLink>
              ) : null}

              {canAccessTools ? <div className="nav-section-label" data-testid="nav-section-tools">Tools</div> : null}
              {canAccessTools ? (
                <NavLink to="/brief" className={navClass} data-testid="nav-brief-link">
                  <Sparkles className={iconClass} />
                  <span className="nav-item-label">Brand Builder</span>
                </NavLink>
              ) : null}
              {canAccessAuditLogs ? (
                <NavLink to="/audit-logs" className={navClass} data-testid="nav-audit-logs-link">
                  <FileText className={iconClass} />
                  <span className="nav-item-label">Audit Logs</span>
                </NavLink>
              ) : null}

              <div className="nav-section-header" data-testid="sidebar-most-viewed-projects-section-header">
                <div className="nav-section-label nav-section-label-inline" data-testid="sidebar-most-viewed-projects-section-label">Most Viewed Projects</div>
                <button
                  type="button"
                  className="sidebar-section-toggle"
                  onClick={() => setIsClientsSectionCollapsed((prev) => !prev)}
                  data-testid="sidebar-most-viewed-projects-section-toggle-button"
                  aria-expanded={!isClientsSectionCollapsed}
                >
                  <ChevronDown className={`h-3.5 w-3.5 ${isClientsSectionCollapsed ? "collapsed" : ""}`} />
                  <span data-testid="sidebar-most-viewed-projects-section-toggle-text">{isClientsSectionCollapsed ? "Show" : "Hide"}</span>
                </button>
              </div>

              {isSidebarCollapsed ? null : isClientsSectionCollapsed ? (
                <div className="nav-item" data-testid="sidebar-most-viewed-projects-collapsed-note">
                  <span className="nav-item-label">Most viewed projects collapsed</span>
                </div>
              ) : projectQuickLinks.length === 0 ? (
                <div className="nav-item" data-testid="sidebar-most-viewed-projects-empty">
                  <span className="nav-item-label">No projects</span>
                </div>
              ) : (
                projectQuickLinks.map((project) => (
                  <NavLink key={project.id} to={`/project/${project.id}`} className="nav-item" data-testid={`sidebar-most-viewed-project-link-${project.id}`}>
                    <span className={healthDotClass(Number(project.health_score || 0))} style={{ marginLeft: 2 }} />
                    <span className="nav-item-label truncate">{project.name}</span>
                    <span style={{ fontSize: 11, color: "var(--mute-500)" }} data-testid={`sidebar-most-viewed-project-link-count-${project.id}`}>
                      {project.view_count}
                    </span>
                  </NavLink>
                ))
              )}
            </>
          )}
        </nav>

        <div className="sidebar-footer" data-testid="sidebar-footer">
          {financeSidebarOnly ? null : (
            <>
              <button
                type="button"
                className={`sidebar-endday-button ${attendanceSessionMeta.isEnded ? "is-ended" : ""} ${isEndingDay ? "is-loading" : ""}`}
                onClick={handleEndDay}
                disabled={isEndingDay}
                data-testid="sidebar-end-day-button"
              >
                <span className={`sidebar-endday-dot ${attendanceSessionMeta.isEnded ? "is-ended" : ""}`} data-testid="sidebar-end-day-status-dot" />
                <span className="sidebar-endday-main" data-testid="sidebar-end-day-main-row">
                  <span className="sidebar-endday-action" data-testid="sidebar-end-day-action-label">{attendanceSessionMeta.actionLabel}</span>
                  <span className="sidebar-endday-range" data-testid="sidebar-end-day-range-label">{attendanceSessionMeta.rangeLabel}</span>
                </span>
              </button>
              {attendanceSessionMeta.summary ? (
                <div className="sidebar-endday-summary" data-testid="sidebar-end-day-summary-card">
                  {attendanceSessionMeta.summary}
                </div>
              ) : null}
            </>
          )}
          <button
            type="button"
            className="user-row"
            style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer" }}
            onClick={() => navigate("/profile")}
            data-testid="sidebar-user-row"
          >
            <div className="user-avatar" style={profileAvatarStyle(user)} data-testid="sidebar-user-avatar">
              {profileAvatarPicture(user) ? (
                <img
                  src={profileAvatarPicture(user)}
                  alt={user.name || user.email}
                  className="h-full w-full rounded-[inherit] object-cover"
                  data-testid="sidebar-user-avatar-image"
                />
              ) : profileAvatar(user)}
            </div>
            <div className="sidebar-user-text" style={{ flex: 1 }}>
              <div className="user-name" data-testid="sidebar-user-name">{user.name || user.email}</div>
              <div className="user-role" data-testid="sidebar-user-role">{user.role.replace("_", " ")}</div>
            </div>
            <ChevronRight className={iconClass} style={{ color: "var(--mute-500)" }} data-testid="sidebar-user-profile-chevron" />
          </button>
          <button className="btn-logout" onClick={onLogout} data-testid="sidebar-logout-button">
            <LogOut className={iconClass} />
            <span className="sidebar-logout-label" data-testid="sidebar-logout-label">Logout</span>
          </button>
        </div>
      </aside>

      <main className="main" data-testid="app-shell-main">
        <header className="topbar" data-testid="app-shell-header">
          <div className="breadcrumb" data-testid="app-shell-breadcrumb">
            <span className="current" data-testid="app-shell-page-title">{pageTitle}</span>
          </div>
          <div className="topbar-right" style={{ gap: 10, overflow: "visible" }} data-testid="app-shell-topbar-actions">
            <div
              ref={searchWrapRef}
              className="search-wrap"
              style={{ width: topbarExtra ? 340 : 420, minWidth: topbarExtra ? 270 : 350, position: "relative" }}
              data-testid="global-search-input-container"
            >
              <span className="search-icon" data-testid="global-search-icon"><Search className="h-3.5 w-3.5" /></span>
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onFocus={() => {
                  if (searchQuery.trim().length >= 2) setIsSearchTrayOpen(true);
                }}
                onKeyDown={onSearchKeyDown}
                className="search-input"
                placeholder={topbarSearchPlaceholder}
                data-testid="global-search-input"
              />
              {searchQuery.trim().length === 0 ? (
                <span className="search-kbd" title="Shortcuts: Cmd/Ctrl+K, /" data-testid="global-search-kbd">⌘K</span>
              ) : (
                <button
                  type="button"
                  className="search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults(emptySearchResults);
                    setIsSearchTrayOpen(false);
                    setActiveSearchIndex(-1);
                    searchInputRef.current?.focus();
                  }}
                  data-testid="global-search-clear-button"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}

              {isSearchTrayOpen ? (
                <div
                  className="absolute left-0 right-0 top-[calc(100%+10px)] rounded-2xl border border-slate-200 bg-white shadow-xl"
                  style={{ zIndex: 120, maxHeight: 460, overflow: "hidden" }}
                  data-testid="global-search-tray"
                >
                  <div className="max-h-[410px] overflow-y-auto" data-testid="global-search-results-scroll">
                    {isSearchLoading ? (
                      <p className="px-4 py-3 text-xs text-slate-500" data-testid="global-search-loading-row">Searching…</p>
                    ) : totalSearchResults === 0 ? (
                      <p className="px-4 py-3 text-xs text-slate-500" data-testid="global-search-empty-row">No results found</p>
                    ) : (
                      groupedSearchResults.map((group) => {
                        if (!group.items.length) return null;
                        return (
                          <section key={group.type} className="border-b border-slate-100 last:border-b-0" data-testid={`global-search-group-${group.type}`}>
                            <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400" data-testid={`global-search-group-heading-${group.type}`}>
                              {group.label}
                            </div>
                            {group.items.map((item) => {
                              const flatIndex = flattenedSearchResults.findIndex((row) => row.type === group.type && row.item.id === item.id && row.item.name === item.name);
                              const isActive = flatIndex === activeSearchIndex;
                              const commonClass = `w-full border-t border-slate-100 px-4 py-2 text-left transition ${isActive ? "bg-slate-50" : "hover:bg-slate-50"}`;

                              if (group.type === "people") {
                                return (
                                  <button
                                    key={`${group.type}-${item.id}`}
                                    type="button"
                                    className={commonClass}
                                    onMouseEnter={() => setActiveSearchIndex(flatIndex)}
                                    onClick={() => handleSearchSelect(group.type, item)}
                                    data-testid={`global-search-result-people-${item.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div
                                        className="grid h-8 w-8 place-items-center rounded-lg text-xs font-semibold"
                                        style={{ background: item.palette?.bg || "#E2E8F0", color: item.palette?.text || "#1E293B" }}
                                        data-testid={`global-search-result-people-avatar-${item.id}`}
                                      >
                                        {(String(item.avatar_mode || "").toLowerCase() === "picture" && item.picture) ? (
                                          <img
                                            src={item.picture}
                                            alt={item.name}
                                            className="h-full w-full rounded-[inherit] object-cover"
                                            data-testid={`global-search-result-people-avatar-image-${item.id}`}
                                          />
                                        ) : (item.avatar_emoji || item.initials)}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-900">{renderHighlightedTitle(item.name, searchQuery)}</p>
                                        <p className="truncate text-xs text-slate-500">{item.role} · {item.workspace}</p>
                                      </div>
                                      <span className="chip chip-mute" data-testid={`global-search-result-people-badge-${item.id}`}>{item.project_role}</span>
                                    </div>
                                  </button>
                                );
                              }

                              if (group.type === "clients") {
                                return (
                                  <button
                                    key={`${group.type}-${item.id}`}
                                    type="button"
                                    className={commonClass}
                                    onMouseEnter={() => setActiveSearchIndex(flatIndex)}
                                    onClick={() => handleSearchSelect(group.type, item)}
                                    data-testid={`global-search-result-clients-${item.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: "#DCFCE7", color: "#166534" }}>
                                        <Briefcase className="h-4 w-4" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-900">{renderHighlightedTitle(item.name, searchQuery)}</p>
                                        <p className="truncate text-xs text-slate-500">{item.industry} · Since {formatSearchDate(item.since)}</p>
                                      </div>
                                      <span className="chip chip-ok" data-testid={`global-search-result-clients-badge-${item.id}`}>Client</span>
                                    </div>
                                  </button>
                                );
                              }

                              if (group.type === "projects") {
                                const color = projectIconColor(item.band);
                                return (
                                  <button
                                    key={`${group.type}-${item.id}`}
                                    type="button"
                                    className={commonClass}
                                    onMouseEnter={() => setActiveSearchIndex(flatIndex)}
                                    onClick={() => handleSearchSelect(group.type, item)}
                                    data-testid={`global-search-result-projects-${item.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${color}20`, color }}>
                                        <FolderKanban className="h-4 w-4" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-900">{renderHighlightedTitle(item.name, searchQuery)}</p>
                                        <p className="truncate text-xs text-slate-500">Health {item.health} · {item.tasks_open} open tasks</p>
                                      </div>
                                      <span className="chip chip-mute" data-testid={`global-search-result-projects-badge-${item.id}`}>{healthBandLabel(item.band)}</span>
                                    </div>
                                  </button>
                                );
                              }

                              if (group.type === "files") {
                                const style = fileTypeStyle(item.file_type);
                                return (
                                  <button
                                    key={`${group.type}-${item.id}`}
                                    type="button"
                                    className={commonClass}
                                    onMouseEnter={() => setActiveSearchIndex(flatIndex)}
                                    onClick={() => handleSearchSelect(group.type, item)}
                                    data-testid={`global-search-result-files-${item.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: style.bg, color: style.text }}>
                                        <FileText className="h-4 w-4" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-semibold text-slate-900">{renderHighlightedTitle(item.name, searchQuery)}</p>
                                        <p className="truncate text-xs text-slate-500">{item.folder} · {item.project_name}</p>
                                      </div>
                                      <span className="chip chip-mute" data-testid={`global-search-result-files-badge-${item.id}`}>{item.file_type}</span>
                                    </div>
                                  </button>
                                );
                              }

                              return (
                                <button
                                  key={`${group.type}-${item.id}`}
                                  type="button"
                                  className={commonClass}
                                  onMouseEnter={() => setActiveSearchIndex(flatIndex)}
                                  onClick={() => handleSearchSelect(group.type, item)}
                                  data-testid={`global-search-result-tasks-${item.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="grid h-8 w-8 place-items-center rounded-lg bg-slate-100 text-slate-600">
                                      <ListTodo className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-semibold text-slate-900">{renderHighlightedTitle(item.name, searchQuery)}</p>
                                      <p className="truncate text-xs text-slate-500">Due {formatSearchDate(item.due)} · {item.assignee_name}</p>
                                    </div>
                                    <span className="chip chip-mute" data-testid={`global-search-result-tasks-badge-${item.id}`}>
                                      {STATUS_LABEL_MAP[item.status] || item.status}
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </section>
                        );
                      })
                    )}
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500" data-testid="global-search-tray-footer">
                    <div className="inline-flex items-center gap-2">
                      <span>↑↓ navigate</span>
                      <span>↵ open</span>
                      <span>Esc close</span>
                    </div>
                    <span data-testid="global-search-result-count">{totalSearchResults} results</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="gamification-streak-wrap" ref={streakWrapRef} data-testid="topbar-streak-wrap">
              <button
                type="button"
                className="gamification-streak-btn"
                onClick={() => {
                  setIsBadgePopoverOpen(false);
                  setIsStreakPopoverOpen((prev) => !prev);
                }}
                data-testid="topbar-streak-button"
              >
                <Flame className="h-4 w-4" />
                <span data-testid="topbar-streak-count">{Number(streakSummary.current_streak || 0)}</span>
              </button>

              {isStreakPopoverOpen ? (
                <div className="gamification-popover gamification-streak-popover" data-testid="topbar-streak-popover">
                  <div className="gamification-popover-title" data-testid="topbar-streak-popover-title">
                    🔥 {Number(streakSummary.current_streak || 0)} day streak
                  </div>
                  <p className="gamification-popover-sub" data-testid="topbar-streak-popover-subtitle">
                    {streakSummary.motivational_copy || "Log in before 10:30 AM on working days to keep your streak."}
                  </p>

                  <div className="gamification-week-grid" data-testid="topbar-streak-week-grid">
                    {(streakSummary.week || []).map((day) => (
                      <div key={day.date} className="gamification-week-day" data-testid={`topbar-streak-week-day-${day.date}`}>
                        <span className="gamification-week-label" data-testid={`topbar-streak-week-label-${day.date}`}>{day.day_label}</span>
                        <span className={`gamification-week-badge ${day.state}`} data-testid={`topbar-streak-week-state-${day.date}`}>
                          {day.state === "done" ? "✓" : day.state === "today" ? "NOW" : day.state === "skip" ? "—" : "•"}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="gamification-popover-footer" data-testid="topbar-streak-popover-footer">
                    {streakSummary.next_milestone ? (
                      <span data-testid="topbar-streak-next-milestone">
                        {streakSummary.days_to_next_milestone || 0} day(s) to {streakSummary.next_milestone}d milestone
                      </span>
                    ) : (
                      <span data-testid="topbar-streak-maxed-note">Highest milestone unlocked 🎉</span>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="gamification-badge-wrap" ref={badgeWrapRef} data-testid="topbar-badge-wrap">
              <button
                type="button"
                className="gamification-badge-btn"
                onClick={() => {
                  setIsStreakPopoverOpen(false);
                  setIsBadgePopoverOpen((prev) => !prev);
                }}
                data-testid="topbar-badge-button"
              >
                <span aria-hidden>🏅</span>
                <span data-testid="topbar-badge-count">{earnedBadges}</span>
              </button>

              {isBadgePopoverOpen ? (
                <div className="gamification-popover gamification-badge-popover" data-testid="topbar-badge-popover">
                  <div className="gamification-popover-title" data-testid="topbar-badge-popover-title">
                    {activeRolePanel.role_label || "Monthly badges"} · {earnedBadges} earned
                  </div>
                  <div className="gamification-earned-list" data-testid="topbar-badge-earned-list">
                    {earnedBadgeRows.length === 0 ? (
                      <p className="gamification-popover-sub" data-testid="topbar-badge-empty-note">No badges earned yet this month.</p>
                    ) : (
                      earnedBadgeRows.slice(0, 4).map((badge) => (
                        <div key={badge.id} className="gamification-earned-item" data-testid={`topbar-badge-earned-item-${badge.id}`}>
                          <span data-testid={`topbar-badge-earned-emoji-${badge.id}`}>{badge.emoji}</span>
                          <span data-testid={`topbar-badge-earned-name-${badge.id}`}>{badge.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="gamification-popover-actions" data-testid="topbar-badge-popover-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setIsBadgePopoverOpen(false);
                        navigate("/dashboard#monthly-badges");
                      }}
                      data-testid="topbar-badge-open-dashboard-button"
                    >
                      Open badges
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setIsBadgePopoverOpen(false);
                        navigate("/leaderboard");
                      }}
                      data-testid="topbar-badge-open-leaderboard-button"
                    >
                      Leaderboard
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="notif-wrap" ref={notificationWrapRef} data-testid="topbar-notification-wrap">
              <button
                type="button"
                className={`notif-bell-btn ${isNotificationsTrayOpen ? "active" : ""}`}
                onClick={toggleNotificationsTray}
                data-testid="topbar-notification-bell-button"
              >
                <Bell className="h-4 w-4" />
                {unreadNotificationCount > 0 ? (
                  <span className="bell-badge" data-testid="topbar-notification-bell-badge">
                    {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
                  </span>
                ) : null}
              </button>

              {isNotificationsTrayOpen ? (
                <div className="notif-tray" data-testid="topbar-notification-tray">
                  <div className="notif-tray-head">
                    <div>
                      <p className="notif-tray-title" data-testid="topbar-notification-tray-title">Notifications</p>
                      <p className="notif-tray-sub" data-testid="topbar-notification-tray-sub">Unread count follows your profile in-app preferences.</p>
                    </div>
                    <button
                      type="button"
                      className="notif-view-all"
                      onClick={() => openNotificationLink({ link: "/notifications" })}
                      data-testid="topbar-notification-view-all-button"
                    >
                      View all
                    </button>
                  </div>

                  <div className="notif-tabs notif-tabs-tray" data-testid="topbar-notification-tabs">
                    {[
                      { id: "all", label: "All" },
                      { id: "leaves", label: "Leaves" },
                      { id: "tasks", label: "Tasks" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`notif-tab-btn ${activeNotificationTab === tab.id ? "active" : ""}`}
                        onClick={() => setActiveNotificationTab(tab.id)}
                        data-testid={`topbar-notification-tab-${tab.id}`}
                      >
                        <span>{tab.label}</span>
                        <span className="notif-tab-count" data-testid={`topbar-notification-tab-count-${tab.id}`}>{trayTabCount(tab.id)}</span>
                      </button>
                    ))}
                  </div>

                  <div className="notif-tray-list" data-testid="topbar-notification-list">
                    {isNotificationsLoading ? (
                      <p className="notif-empty" data-testid="topbar-notification-loading">Loading notifications…</p>
                    ) : trayNotifications.length === 0 ? (
                      <p className="notif-empty" data-testid="topbar-notification-empty">No notifications found.</p>
                    ) : (
                      trayNotifications.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className={`notif-row ${item.status === "read" ? "read" : "unread"}`}
                          onClick={() => openNotificationLink(item)}
                          data-testid={`topbar-notification-row-${item.id}`}
                        >
                          <div className="notif-row-body">
                            <p className="notif-row-title" data-testid={`topbar-notification-row-title-${item.id}`}>{item.title}</p>
                            <p className="notif-row-subtitle" data-testid={`topbar-notification-row-subtitle-${item.id}`}>{item.subtitle || ""}</p>
                          </div>
                          <div className="notif-row-right" data-testid={`topbar-notification-row-time-${item.id}`}>
                            {item.time_label}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {topbarExtra ? <div data-testid="app-shell-topbar-extra">{topbarExtra}</div> : null}

            {canCreateTask && !hideGlobalNewTask ? (
              <button type="button" className="btn btn-primary topbar-new-task-btn" title="Shortcut: Cmd/Ctrl+N" onClick={() => setIsAddTaskModalOpen(true)} data-testid="global-new-task-button">
                + New Task
              </button>
            ) : null}
          </div>
        </header>

        <div className="page" data-testid="app-shell-page-container">{children}</div>
      </main>

      {canCreateTask ? (
        <AddTaskModal
          open={isAddTaskModalOpen}
          onOpenChange={setIsAddTaskModalOpen}
          user={user}
          defaultProjectId={activeProjectId}
          onTaskCreated={() => setSearchQuery("")}
        />
      ) : null}

      <Dialog open={isEndDayConfirmOpen} onOpenChange={setIsEndDayConfirmOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="sidebar-end-day-confirm-modal">
          <DialogHeader>
            <DialogTitle data-testid="sidebar-end-day-confirm-modal-title">End day again?</DialogTitle>
            <DialogDescription data-testid="sidebar-end-day-confirm-modal-description">
              We already have a saved end time for today. Confirm once to overwrite it with the latest timestamp.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsEndDayConfirmOpen(false)} data-testid="sidebar-end-day-confirm-cancel-button">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                setIsEndDayConfirmOpen(false);
                await executeEndDay();
              }}
              disabled={isEndingDay}
              data-testid="sidebar-end-day-confirm-submit-button"
            >
              {isEndingDay ? "Saving..." : "Yes, end day"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
